import { captureException } from "@sentry/react";
import { invalidateTokens } from "@sunrise/auth";
import { growthbookAtom } from "@sunrise/feature-flags";
import { hostsAtom, httpClientAtom } from "@sunrise/http-client";
import { currentLanguageAtom } from "@sunrise/i18n";
import { actionJWTClear, actionJWTSetTokens, jwtAtom } from "@sunrise/jwt";
import {
  allowParallelPrefetchAtom,
  trackKnownBandwidthAtom,
} from "@sunrise/player";
import {
  actionPlayerStatsToggleEnabled,
  playerStatsAtom,
  selectPlayerStatsEnabled,
} from "@sunrise/player-stats";
import { processVisibilityAtom } from "@sunrise/process-visibility";
import { mockDataAtom } from "@sunrise/yallo-settings";
import { forceLiveTvTimeLimitReachedAtom } from "@sunrise/yallo-user";
import {
  socketAuthenticatedAtom,
  socketConnectedAtom,
} from "@sunrise/yallo-websocket";
import { formatDuration, intervalToDuration } from "date-fns";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { isNil } from "lodash";
import { type ReactElement, useCallback, useState } from "react";

import { FocusButton } from "@/components";
import { globalFocusKey } from "@/config/focus-key";
import { useMenu } from "@/features/menu/use-menu";
import { useRoutes } from "@/features/routing/use-routes";
import { useCyclicControl } from "@/modules/developer/use-cyclic-control";
import {
  actionGuideClearSelection,
  gridStateAtom,
} from "@/modules/guide/store";
import { memoryStatusAtom } from "@/modules/memory/memory-status.atom";
import { memoryUsageAtom } from "@/modules/memory/memory-usage.atom";
import { tizenProductInfoAtom } from "@/modules/tizen";
import {
  closeApp,
  readTizenPlatformVersion,
} from "@/modules/tizen/system.service";
import { FocusContainer, FocusableOnArrowPress } from "@/utils/focus-container";

import {
  button,
  container,
  happyTestingStyle,
  root,
  row,
  systemInfos,
} from "./developer-controls.css";
import { asBoolean } from "./developer.utils";
import { languages } from "@sunrise/backend-types-core";
import { LoggedOutError } from "@sunrise/backend-core";

const EXPIRED_DEVELOPMENT_ACCESS_TOKEN =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.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.lfqt74TbPzvQ9M-VzJPIdxN4LDwA4hQJ_5Wx7zfiFxw";
const EXPIRED_DEVELOPMENT_REFRESH_TOKEN =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJkZXZpY2VfdHlwZV9pZCI6IjAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwOSIsInVzZXJfaWQiOiIwMDAwMDAwMDAwMDAwMDAwMDAwMDIyMDIiLCJ0b2tlbl90cmFja2luZ19pZCI6IjU2MDRjNDI0MGE5ODRiODBiZmE3YzQyNTExNGQ1NDhhIiwiaWF0Ijo0NzMwMzY0MDAsImV4cCI6NDczMDM2NDAwLCJhdWQiOiJ3aWxtYWE6Y29zbW9zX3JlZnJlc2giLCJpc3MiOiJ3aWxtYWE6Y29zbW9zX2thbWlub19yZWZyZXNoIiwiX3IiOjQ0NTc5Nn0.5kOHPXeDtzbn7Ldvs8lVpbMUrauEAaurnqTn8oXx5Wg";

export function DeveloperControls(): ReactElement {
  // It's forbidden to show menu on this page
  useMenu({ hidden: true });
  const routes = useRoutes();

  const httpClient = useAtomValue(httpClientAtom);

  const dispatchGridState = useSetAtom(gridStateAtom);
  const [usingMockData, setUsingMockData] = useAtom(mockDataAtom);
  const toggleMockData = useCallback(() => {
    setUsingMockData((v) => !v);
    dispatchGridState(actionGuideClearSelection());
  }, [setUsingMockData, dispatchGridState]);

  const dispatchJWT = useSetAtom(jwtAtom);
  const doLogOut = useCallback(() => {
    captureException(new LoggedOutError("user_requested_logout"));
    dispatchJWT(actionJWTClear());
  }, [dispatchJWT]);

  const lang = useCyclicControl(currentLanguageAtom, languages);

  const [forceLiveTvLimitReached, setForceLiveTvLimitReached] = useAtom(
    forceLiveTvTimeLimitReachedAtom,
  );
  const toggleForceLiveTvLimitReached = useCallback(() => {
    setForceLiveTvLimitReached((v) => !v);
  }, [setForceLiveTvLimitReached]);

  const [allowParallelPrefetch, setAllowParallelPrefetch] = useAtom(
    allowParallelPrefetchAtom,
  );
  const toggleAllowParallelPrefetch = useCallback(() => {
    setAllowParallelPrefetch((v) => !v);
  }, [setAllowParallelPrefetch]);

  const [trackBandwidth, setTrackBandwidth] = useAtom(trackKnownBandwidthAtom);
  const toggleTrackBandwidth = useCallback(() => {
    setTrackBandwidth((v) => !v);
  }, [setTrackBandwidth]);

  const playerStats = useAtomValue(selectPlayerStatsEnabled);
  const dispatchPlayerStats = useSetAtom(playerStatsAtom);
  const togglePlayerStats = useCallback(() => {
    dispatchPlayerStats(actionPlayerStatsToggleEnabled());
  }, [dispatchPlayerStats]);

  const hosts = useAtomValue(hostsAtom);

  async function invalidateAccessToken(): Promise<void> {
    const host = hosts.api;
    if (isNil(host)) throw new Error("Empty host");

    if (isNil(httpClient.privateApi)) throw new Error("Empty private api");

    await invalidateTokens(httpClient.privateApi, host, "access_token_only");
  }

  async function invalidateAllTokens(): Promise<void> {
    const host = hosts.api;
    if (isNil(host)) throw new Error("Empty host");

    if (isNil(httpClient.privateApi)) throw new Error("Empty private api");

    await invalidateTokens(httpClient.privateApi, host, "all");
  }

  const isSocketAuthenticated = useAtomValue(socketAuthenticatedAtom);
  const isSocketConnected = useAtomValue(socketConnectedAtom);
  const memoryStatus = useAtomValue(memoryStatusAtom);
  const memoryUsage = useAtomValue(memoryUsageAtom);

  function expireTokens(): void {
    dispatchJWT(
      actionJWTSetTokens({
        accessToken: EXPIRED_DEVELOPMENT_ACCESS_TOKEN,
        refreshToken: EXPIRED_DEVELOPMENT_REFRESH_TOKEN,
      }),
    );
  }

  const { data: productInfo } = useAtomValue(tizenProductInfoAtom);

  const tizenVersion = readTizenPlatformVersion();

  const gb = useAtomValue(growthbookAtom);

  const processVisibility = useAtomValue(processVisibilityAtom);

  return (
    <div className={root}>
      <div className={happyTestingStyle}>🐣🌷🐰🎉 Happy Testing 🎉🐰🌷🐣</div>
      <FocusContainer
        focusKey={globalFocusKey.activePage}
        onBack={routes.back}
        className={container}
        shouldFocus
      >
        {(directions) => (
          <>
            <div className={row}>
              <FocusButton
                className={button}
                text="log out"
                onEnterPress={doLogOut}
                onArrowPress={directions.onArrowPress("left")}
              />
              <FocusButton
                className={button}
                text="close app"
                onEnterPress={closeApp}
              />
              <AsyncActionButton
                text="invalidate access token"
                onEnterPress={invalidateAccessToken}
              />
              <AsyncActionButton
                text="invalidate all tokens"
                onEnterPress={invalidateAllTokens}
              />
              {import.meta.env.MODE === "development" ? (
                <FocusButton
                  className={button}
                  text="expire tokens"
                  onEnterPress={expireTokens}
                />
              ) : null}
            </div>
            <div className={row}>
              <ToggleButton
                text="mock data"
                isActive={usingMockData}
                onEnterPress={toggleMockData}
              />
            </div>
            <div className={row}>
              <ToggleButton
                text="player stats"
                isActive={playerStats}
                onEnterPress={togglePlayerStats}
              />
              <ToggleButton
                text="parallel prefetch"
                isActive={allowParallelPrefetch}
                onEnterPress={toggleAllowParallelPrefetch}
              />
              <ToggleButton
                text="track bandwidth"
                isActive={trackBandwidth}
                onEnterPress={toggleTrackBandwidth}
              />
            </div>
            <div className={row}>
              <FocusButton
                key={`language`}
                className={button}
                focusKey={`language`}
                text={`language: ${lang.value}`}
                onEnterPress={lang.next}
                onArrowPress={directions.onArrowPress("left")}
              />
            </div>
            <div className={row}>
              <ToggleButton
                text="force live tv limit reached"
                isActive={forceLiveTvLimitReached}
                onEnterPress={toggleForceLiveTvLimitReached}
                onArrowPress={directions.onArrowPress("left")}
              />
              <FocusButton
                text="throw error"
                className={button}
                onEnterPress={() => {
                  throw new Error("BOOM");
                }}
              />
            </div>
            {productInfo && (
              <div className={systemInfos}>
                <div>Tizen Version: {tizenVersion}</div>
                <div>DUID: {productInfo.duid}</div>
                <div>Model: {productInfo.model}</div>
                <div>Model Code: {productInfo.modelCode}</div>
                <div>Real Model: {productInfo.realModel}</div>
                <div>Firmware: {productInfo.firmware}</div>
              </div>
            )}
            <div className={systemInfos}>
              <div>Websocket Connected: {asBoolean(isSocketConnected)}</div>
              <div>
                Websocket Authenticated: {asBoolean(isSocketAuthenticated)}
              </div>
              <div>
                Growthbook ready: {gb ? asBoolean(gb.instance.ready) : false}
              </div>
              {memoryStatus ? <div>memory status: {memoryStatus}</div> : null}
              {memoryUsage ? (
                <div>
                  memory usage: {bytesAsMB(memoryUsage.used)}Mb /{" "}
                  {bytesAsMB(memoryUsage.total)}Mb (
                  {Math.floor((memoryUsage.used / memoryUsage.total) * 100)}%)
                </div>
              ) : null}
              <div>
                process-visibility: foregrounded:{" "}
                {processVisibility.lastForegroundedAt.toLocaleString()},
                backgrounded:{" "}
                {processVisibility.lastBackgroundedAt?.toLocaleString() ??
                  "not yet"}
                {processVisibility.lastBackgroundedAt
                  ? `, time spent in bg: ${formatDuration(
                      intervalToDuration({
                        start: processVisibility.lastBackgroundedAt,
                        end: processVisibility.lastForegroundedAt,
                      }),
                    )}`
                  : null}
              </div>
            </div>
          </>
        )}
      </FocusContainer>
    </div>
  );
}

function bytesAsMB(bytes: number): number {
  return Math.round(bytes / 1024 / 1024);
}

/**
 * TODO: refactor "toggle" buttons to actually show as toggles. (if we ever introduce a toggle component)
 */
function ToggleButton({
  text,
  isActive,
  onArrowPress,
  onEnterPress,
}: {
  text: string;
  isActive: boolean;
  onEnterPress: () => void;
  onArrowPress?: FocusableOnArrowPress;
}): ReactElement {
  return (
    <FocusButton
      className={button}
      text={`${text}: ${isActive ? "on" : "off"}`}
      onEnterPress={onEnterPress}
      onArrowPress={onArrowPress}
    />
  );
}

function AsyncActionButton({
  onEnterPress,
  text,
}: {
  onEnterPress: () => Promise<void>;
  text: string;
}): ReactElement {
  const [disabled, setDisabled] = useState(false);

  const action = useCallback(async (): Promise<void> => {
    setDisabled(true);
    try {
      await onEnterPress();
    } finally {
      setDisabled(false);
    }
  }, [onEnterPress]);

  return (
    <FocusButton
      className={button}
      disabled={disabled}
      text={text}
      onEnterPress={() => void action()}
    />
  );
}
